import { useMemo } from 'react';
import type { HeatmapCell } from '@/lib/types';

interface HeatmapChartProps {
  data: HeatmapCell[];
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function HeatmapChart({ data }: HeatmapChartProps) {
  const { grid, max } = useMemo(() => {
    const cells = new Map<string, number>();
    let max = 0;
    for (const c of data) {
      cells.set(`${c.dow}:${c.hour}`, c.count);
      if (c.count > max) max = c.count;
    }
    return { grid: cells, max };
  }, [data]);

  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No data in this range.
      </p>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[640px] space-y-1.5">
        <div className="grid grid-cols-[36px_repeat(24,minmax(0,1fr))] items-center gap-1 text-[10px] text-muted-foreground">
          <div />
          {HOURS.map((h) => (
            <div key={h} className="text-center tabular-nums">
              {h % 3 === 0 ? h.toString().padStart(2, '0') : ''}
            </div>
          ))}
        </div>
        {DAYS.map((day, dow) => (
          <div
            key={day}
            className="grid grid-cols-[36px_repeat(24,minmax(0,1fr))] items-center gap-1"
          >
            <div className="text-xs text-muted-foreground">{day}</div>
            {HOURS.map((hour) => {
              const count = grid.get(`${dow}:${hour}`) ?? 0;
              const intensity = max === 0 ? 0 : count / max;
              return (
                <div
                  key={hour}
                  className="aspect-square rounded-sm border border-border bg-primary"
                  style={{
                    opacity: count === 0 ? 0.05 : 0.15 + intensity * 0.85,
                  }}
                  title={`${day} ${hour.toString().padStart(2, '0')}:00 — ${count}`}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
